import React, { FC, useEffect, useMemo, useState } from 'react';
import { styled } from '@mui/material';
import { Box, Card, CardContent, CardHeader, Link } from '@mui/material';
import { Grid } from '@mui/system';
import { isEmpty } from 'lodash';
import { useSelector } from 'react-redux';
import { useFormat } from 'common/hooks';
import { REGULAR_UNIT } from 'components/FeaturedSpreadsheet/constants';
import { ValuationMethodsDetail } from 'dashboard409a/components/ValuationMethodsDetail';
import { ValuationMethodsDetailProps } from 'dashboard409a/components/ValuationMethodsDetail/types';
import { IValuationState } from 'dashboard409a/states/features/valuation';
import { RootStateType } from 'dashboard409a/states/store';
import { formatMD } from 'dashboard409a/utilities';
import { VOSDProps } from './types';
import { EnterpriseValue } from '../EnterpriseValue';
import { EnterpriseValueProps } from '../EnterpriseValue/types';
import { ReportsCardHeader } from '../ReportsCardHeader';

const PREFIX = 'ValuationOpinionSummaryData';

const classes = {
  root: `${PREFIX}-root`,
  link: `${PREFIX}-link`,
  boxValOpinion: `${PREFIX}-boxValOpinion`,
};

const StyledGrid = styled(Grid)(() => ({
  [`& .${classes.root}`]: {
    marginBottom: '2%',
    boxShadow: 'none',
  },

  [`& .${classes.link}`]: {
    display: 'inline',
    cursor: 'pointer',
  },

  [`& .${classes.boxValOpinion}`]: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: '2%',
  },
}));

const ValuationOpinionSummaryData: FC<VOSDProps> = ({ showValMethods, displayTable, shareSpace }) => {
  const { loading, currentValuationScalarOpinion } = useSelector<RootStateType, IValuationState>(
    state => state.valuation
  );
  const { approaches } = currentValuationScalarOpinion || {};
  const hasApproaches = !isEmpty(approaches);
  const { currentMeasurementDate } = useSelector((state: RootStateType) => state.global);
  const currentDateValue = useMemo(() => currentMeasurementDate?.date, [currentMeasurementDate]);
  const [format, formatDispatch] = useFormat({ units: REGULAR_UNIT });
  const lgColumns = shareSpace ? 6 : 12;

  const updatedAsDate = useMemo(() => {
    if (currentMeasurementDate) {
      return formatMD({ date: currentDateValue });
    }
    return '';
  }, [currentDateValue, currentMeasurementDate]);

  const [updatedValuationScalarOpinion, setUpdatedValuationScalarOpinion] = useState({
    weighted_market_enterprise_value: '',
    marketable_value_per_common_share: '',
  });

  const valuationMethodsDetailProps: ValuationMethodsDetailProps = {
    data: currentValuationScalarOpinion,
    isLoading: loading,
    format,
    formatDispatch,
    updatedValuationScalarOpinion,
    setUpdatedValuationScalarOpinion,
  };

  const enterpriseValueProps: EnterpriseValueProps = {
    enterpriseData: updatedValuationScalarOpinion,
    isLoading: loading,
    format,
  };

  useEffect(() => {
    if (currentValuationScalarOpinion) {
      setUpdatedValuationScalarOpinion({
        weighted_market_enterprise_value: currentValuationScalarOpinion.weighted_market_enterprise_value,
        marketable_value_per_common_share: currentValuationScalarOpinion.marketable_value_per_common_share,
      });
    }
  }, [currentValuationScalarOpinion]);

  return (
    <StyledGrid size={{ xs: 12, lg: lgColumns }}>
      <Card className={classes.root}>
        <CardHeader title={<ReportsCardHeader title="Valuation Opinion" />} subheader={<></>} disableTypography />
        <CardContent>
          <EnterpriseValue {...enterpriseValueProps} />
          <Box className={classes.boxValOpinion} display="flex" justifyContent="space-between" alignItems="center">
            <small style={{ color: '#8A9097', fontSize: '0.75rem', fontStyle: 'italic' }}>
              Scalar opinion updated as of {updatedAsDate}.
            </small>
            {hasApproaches && (
              <Link
                id="show-valuation-methods-link"
                onClick={() => displayTable('valuationOpinion')}
                className={classes.link}
                variant="body2">
                {`${showValMethods ? 'Hide' : 'Show'} Valuation Methods`}
              </Link>
            )}
          </Box>
        </CardContent>
      </Card>
      {showValMethods && hasApproaches ? (
        <Card>
          <CardContent>
            <ValuationMethodsDetail {...valuationMethodsDetailProps} />
          </CardContent>
        </Card>
      ) : (
        <></>
      )}
    </StyledGrid>
  );
};

export default ValuationOpinionSummaryData;
